import { useCallback, useEffect, useRef } from 'react';
import { isWeb } from '@helpers';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { getDemoConfig } from '@components/tours/config';
import { cleanupPlanboardDemo } from '@components/tours/config/planboard/demoData';
import { cleanupBatchDemo } from '@components/tours/config/machines/batch/demoData';
import { removeDemoMachine } from '@reducers/machines';
import { DEMO_WELCOME_MACHINE_ID } from '@components/tours/config/global/welcome/constants';
import { clearDemoRegistry } from '@components/tours/helpers/demoDataRegistry';
import { delay } from '@components/tours/helpers';
import type { DemoTourConfig } from '@components/tours/types';

export const useTourDemoData = () => {
  const dispatch = useAppDispatch();
  const activeDemoConfigRef = useRef<DemoTourConfig | null>(null);

  const cleanupDemoData = useCallback(() => {
    if (activeDemoConfigRef.current) {
      dispatch(activeDemoConfigRef.current.cleanup());
      activeDemoConfigRef.current = null;
    }
  }, [dispatch]);

  const injectDemoData = useCallback(
    async (tourId: string) => {
      const demoConfig = getDemoConfig(tourId);

      if (demoConfig) {
        activeDemoConfigRef.current = demoConfig;
        dispatch(demoConfig.inject());

        if (demoConfig.injectDelay) {
          await delay(demoConfig.injectDelay);
        }
      }
    },
    [dispatch]
  );

  // Cleanup stale demo data on mount and active demo data on unmount
  useEffect(() => {
    if (!isWeb) {
      return;
    }

    // Run cleanup after a short delay to allow redux-persist to rehydrate
    const timeoutId = setTimeout(() => {
      dispatch(cleanupPlanboardDemo());
      dispatch(cleanupBatchDemo());
      // Clean up stale welcome tour demo machine (persisted from interrupted tour)
      dispatch(removeDemoMachine(DEMO_WELCOME_MACHINE_ID));
      clearDemoRegistry();
    }, 500);

    return () => {
      clearTimeout(timeoutId);
      if (activeDemoConfigRef.current) {
        dispatch(activeDemoConfigRef.current.cleanup());
        activeDemoConfigRef.current = null;
      }
    };
  }, [dispatch]);

  return {
    cleanupDemoData,
    injectDemoData,
  };
};
